import React, { useState, useEffect, useCallback } from 'react';
import { SurveyDefinition, DynamicSurveyResponse } from '../../types/survey.types';
import QuestionRenderer from './renderers/QuestionRenderer';
import { addBreadcrumb } from '../../utils/sentry';
import { evaluateAllLogic, QuestionState } from '../../utils/logicEvaluator';

interface DynamicSurveyRendererProps {
  surveyDefinition: SurveyDefinition;
  onComplete: (responses: DynamicSurveyResponse) => void;
  onCancel?: () => void;
}

interface ValidationErrors {
  [questionId: string]: string;
}

const DynamicSurveyRenderer: React.FC<DynamicSurveyRendererProps> = ({
  surveyDefinition,
  onComplete,
  onCancel,
}) => {
  // Section navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  // Response state - stores all answers
  const [responses, setResponses] = useState<Record<string, any>>({});

  // Question states - tracks visibility and required status based on logic
  const [questionStates, setQuestionStates] = useState<Map<string, QuestionState>>(new Map());

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Track start time for completion analytics
  const [startTime] = useState(Date.now());

  // Session ID for duplicate detection
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const currentSection = surveyDefinition.sections[currentSectionIndex];
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === surveyDefinition.sections.length - 1;
  const progress = ((currentSectionIndex + 1) / surveyDefinition.sections.length) * 100;

  // Auto-save to localStorage
  useEffect(() => {
    const saveKey = `survey_draft_${surveyDefinition.id}`;

    // Save current progress
    if (Object.keys(responses).length > 0) {
      localStorage.setItem(saveKey, JSON.stringify({
        responses,
        sectionIndex: currentSectionIndex,
        timestamp: Date.now(),
      }));
      addBreadcrumb('Auto-saved survey progress', 'survey', {
        surveyId: surveyDefinition.id,
        questionCount: Object.keys(responses).length,
      });
    }

    // Cleanup on unmount
    return () => {
      // Optionally clear draft on submit
    };
  }, [responses, currentSectionIndex, surveyDefinition.id]);

  // Restore draft on mount
  useEffect(() => {
    const saveKey = `survey_draft_${surveyDefinition.id}`;
    const savedData = localStorage.getItem(saveKey);

    if (savedData) {
      try {
        const { responses: savedResponses, sectionIndex, timestamp } = JSON.parse(savedData);

        // Only restore if less than 24 hours old
        const isRecent = Date.now() - timestamp < 24 * 60 * 60 * 1000;

        if (isRecent && window.confirm('We found a previously saved draft. Would you like to continue where you left off?')) {
          setResponses(savedResponses);
          setCurrentSectionIndex(sectionIndex);
          addBreadcrumb('Restored survey draft', 'survey', { surveyId: surveyDefinition.id });
        }
      } catch (error) {
        console.error('Failed to restore draft:', error);
      }
    }
  }, [surveyDefinition.id]);

  // Evaluate conditional logic whenever responses change
  useEffect(() => {
    // Get all questions from all sections
    const allQuestions = surveyDefinition.sections.flatMap(section => section.questions);

    // Evaluate logic rules
    const newQuestionStates = evaluateAllLogic(allQuestions, responses);

    setQuestionStates(newQuestionStates);

    // Check for jump_to actions
    allQuestions.forEach((question) => {
      if (!question.logicRules || question.logicRules.length === 0) return;

      question.logicRules.forEach((rule) => {
        if (!rule.enabled) return;

        // Check if rule conditions are met
        const ruleMatches = rule.ruleGroups.some((group) => {
          const conditionResults = group.conditions.map((condition) => {
            const responseValue = responses[condition.questionId];
            // Simple condition check (you can expand this based on your logic evaluator)
            return responseValue !== undefined && responseValue !== null && responseValue !== '';
          });

          return group.operator === 'AND'
            ? conditionResults.every((r) => r)
            : conditionResults.some((r) => r);
        });

        if (ruleMatches) {
          // Look for jump_to actions
          rule.actions.forEach((action) => {
            if (action.type === 'jump_to' && action.targetSectionId) {
              // Find the target section index
              const targetSectionIndex = surveyDefinition.sections.findIndex(
                (section) => section.id === action.targetSectionId
              );

              if (targetSectionIndex !== -1 && targetSectionIndex !== currentSectionIndex) {
                setCurrentSectionIndex(targetSectionIndex);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                addBreadcrumb('Jumped to section via logic', 'survey', {
                  targetSectionIndex,
                  ruleId: rule.id,
                });
              }
            }
          });
        }
      });
    });

    addBreadcrumb('Evaluated conditional logic', 'survey', {
      surveyId: surveyDefinition.id,
      responseCount: Object.keys(responses).length,
      visibleQuestionCount: Array.from(newQuestionStates.values()).filter(state => state.visible).length,
    });
  }, [responses, surveyDefinition, currentSectionIndex]);

  /**
   * Handle answer change for a question
   */
  const handleResponseChange = useCallback((questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value,
    }));

    // Clear validation error for this question
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  }, []);

  /**
   * Validate current section
   */
  const validateSection = useCallback((): boolean => {
    const errors: ValidationErrors = {};

    // Check all questions in current section
    currentSection.questions.forEach((question) => {
      // Get question state (visibility and required status from logic)
      const questionState = questionStates.get(question.id);
      const isVisible = questionState?.visible !== false;
      const isRequired = questionState?.required || question.required;

      // Skip validation for hidden questions
      if (!isVisible) {
        return;
      }

      const value = responses[question.id];

      // Check required fields (using conditional logic-based required status)
      if (isRequired) {
        if (value === undefined || value === null || value === '') {
          errors[question.id] = 'This field is required';
        } else if (Array.isArray(value) && value.length === 0) {
          errors[question.id] = 'Please select at least one option';
        } else if (question.type === 'dosing_schedule' && typeof value === 'object') {
          if (!value.daysOn || !value.cycleDuration) {
            errors[question.id] = 'Please complete the dosing schedule';
          }
        } else if (question.type === 'measurement' && typeof value === 'object') {
          if (!value.amount || !value.unit) {
            errors[question.id] = 'Please enter an amount and select a unit';
          }
        }
      }

      // Email validation
      if (question.type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[question.id] = 'Please enter a valid email address';
        }
      }

      // Number validation
      if (question.type === 'number' && value !== undefined && value !== '') {
        const numValue = Number(value);
        const min = question.settings?.minValue;
        const max = question.settings?.maxValue;

        if (isNaN(numValue)) {
          errors[question.id] = 'Please enter a valid number';
        } else if (min !== undefined && numValue < min) {
          errors[question.id] = `Value must be at least ${min}`;
        } else if (max !== undefined && numValue > max) {
          errors[question.id] = `Value must be no more than ${max}`;
        }
      }

      // Text length validation
      if ((question.type === 'text' || question.type === 'textarea') && value) {
        const maxLength = question.settings?.maxLength;

        // Find minLength validation rule if exists
        const minLengthRule = question.validationRules?.find(r => r.type === 'minLength');
        const minLength = minLengthRule?.value;

        if (minLength && value.length < minLength) {
          errors[question.id] = minLengthRule.message || `Minimum ${minLength} characters required`;
        } else if (maxLength && value.length > maxLength) {
          errors[question.id] = `Maximum ${maxLength} characters allowed`;
        }
      }

      // Apply custom validation rules
      if (question.validationRules && value) {
        for (const rule of question.validationRules) {
          if (rule.type === 'pattern' && rule.value) {
            try {
              const regex = new RegExp(rule.value as string);
              if (!regex.test(value)) {
                errors[question.id] = rule.message || 'Invalid format';
                break;
              }
            } catch (error) {
              console.error('Invalid regex pattern:', error);
            }
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [currentSection, responses, questionStates]);

  /**
   * Navigate to next section
   */
  const handleNext = useCallback(() => {
    if (!validateSection()) {
      // Scroll to first error
      const firstErrorElement = document.querySelector('.error-message');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!isLastSection) {
      setCurrentSectionIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      addBreadcrumb('Navigated to next section', 'survey', { sectionIndex: currentSectionIndex + 1 });
    }
  }, [validateSection, isLastSection, currentSectionIndex]);

  /**
   * Navigate to previous section
   */
  const handlePrevious = useCallback(() => {
    if (!isFirstSection) {
      setCurrentSectionIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      addBreadcrumb('Navigated to previous section', 'survey', { sectionIndex: currentSectionIndex - 1 });
    }
  }, [isFirstSection, currentSectionIndex]);

  /**
   * Submit survey
   */
  const handleSubmit = useCallback(() => {
    if (!validateSection()) {
      // Scroll to first error
      const firstErrorElement = document.querySelector('.error-message');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Calculate completion time
    const completionTime = Math.floor((Date.now() - startTime) / 1000); // in seconds

    // Build response object
    // Convert simple responses to structured format
    const structuredResponses: Record<string, any> = {};
    Object.keys(responses).forEach(questionId => {
      const question = surveyDefinition.sections
        .flatMap(s => s.questions)
        .find(q => q.id === questionId);
      
      if (question) {
        structuredResponses[questionId] = {
          value: responses[questionId],
          questionType: question.type,
          answeredAt: new Date() as any,
          skipped: false,
        };
      }
    });

    const surveyResponse: DynamicSurveyResponse = {
      surveyId: surveyDefinition.id,
      surveyVersion: surveyDefinition.version,
      responses: structuredResponses,
      metadata: {
        submittedAt: new Date() as any,
        startedAt: new Date(startTime) as any,
        completionTime,
        userAgent: navigator.userAgent,
        sessionId,
      },
      status: 'completed',
    };

    // Clear draft
    localStorage.removeItem(`survey_draft_${surveyDefinition.id}`);

    addBreadcrumb('Survey submitted', 'survey', {
      surveyId: surveyDefinition.id,
      responseCount: Object.keys(responses).length,
      completionTime,
    });

    onComplete(surveyResponse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateSection, surveyDefinition.id, responses, startTime, sessionId, onComplete]);

  /**
   * Handle cancel
   */
  const handleCancelClick = useCallback(() => {
    if (Object.keys(responses).length > 0) {
      if (window.confirm('Are you sure you want to cancel? Your progress will be saved as a draft.')) {
        onCancel?.();
      }
    } else {
      onCancel?.();
    }
  }, [responses, onCancel]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{surveyDefinition.title}</h1>
        {surveyDefinition.description && (
          <div
            className="text-gray-600"
            style={{ whiteSpace: 'pre-line' }}
            dangerouslySetInnerHTML={{ __html: surveyDefinition.description }}
          />
        )}

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Section {currentSectionIndex + 1} of {surveyDefinition.sections.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Current Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{currentSection.title}</h2>
        {currentSection.description && (
          <div
            className="text-gray-600 mb-6"
            style={{ whiteSpace: 'pre-line' }}
            dangerouslySetInnerHTML={{ __html: currentSection.description }}
          />
        )}

        {/* Questions */}
        <div className="space-y-8">
          {currentSection.questions.map((question, index) => {
            // Check if question is visible based on conditional logic
            const questionState = questionStates.get(question.id);
            const isVisible = questionState?.visible !== false;

            // Skip hidden questions
            if (!isVisible) {
              return null;
            }

            // Apply conditional required status
            const isRequired = questionState?.required || question.required;
            const questionWithLogic = { ...question, required: isRequired };

            return (
              <div key={question.id} className="question-item">
                <QuestionRenderer
                  question={questionWithLogic}
                  value={responses[question.id]}
                  onChange={(value) => handleResponseChange(question.id, value)}
                  error={validationErrors[question.id]}
                  questionNumber={index + 1}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <div>
          {onCancel && (
            <button
              onClick={handleCancelClick}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>

        <div className="flex space-x-4">
          {!isFirstSection && (
            <button
              onClick={handlePrevious}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
            >
              Previous
            </button>
          )}

          {!isLastSection ? (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              Next Section
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors shadow-md"
            >
              Submit Survey
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicSurveyRenderer;
